import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight } from "lucide-react";
import DashboardMock from "@/components/DashboardMock";

export default function Hero() {
  return (
    <section data-testid="hero-section" className="relative overflow-hidden pt-16 pb-24 lg:pb-32">
      {/* Ambient blobs */}
      <span className="hero-blob bg-blue-200 top-[-120px] left-[-120px] w-[420px] h-[420px]" />
      <span className="hero-blob bg-indigo-100 top-[20%] right-[-160px] w-[420px] h-[420px] opacity-60" />

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-16 lg:pt-24">
        <div className="grid lg:grid-cols-12 gap-12 lg:gap-16 items-center">
          <div className="lg:col-span-6">
            <motion.div
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              className="inline-flex items-center gap-2 bg-white border border-slate-200 rounded-full px-3.5 py-1.5 text-xs font-medium text-slate-700 shadow-sm"
              data-testid="hero-eyebrow"
            >
              <span className="w-1.5 h-1.5 rounded-full bg-blue-600 pulse-dot" />
              Operational infrastructure for cash-pay clinics
            </motion.div>

            <motion.h1
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.05 }}
              className="mt-6 font-display font-extrabold tracking-tighter text-slate-900 text-4xl sm:text-5xl lg:text-[64px] leading-[1.02]"
              data-testid="hero-headline"
            >
              The Operating System for{" "}
              <span className="relative inline-block">
                <span className="relative z-10 bg-gradient-to-r from-slate-900 via-blue-700 to-slate-900 bg-clip-text text-transparent">
                  Cash-Pay Healthcare Growth
                </span>
              </span>
            </motion.h1>

            <motion.p
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.12 }}
              className="mt-6 text-lg text-slate-600 leading-relaxed max-w-xl"
              data-testid="hero-subheadline"
            >
              Launch and scale high-margin cash-pay programs in your clinic through curated services, vendor coordination, workflow automation, and embedded financial infrastructure.
            </motion.p>

            <motion.p
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="mt-4 text-sm text-slate-500 max-w-xl leading-relaxed"
            >
              Deploy turnkey programs built on services and products clinics already acquire — now powered by Nodera's vendor network and the competitive contracted rates we've negotiated on your behalf.
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.28 }}
              className="mt-9 flex flex-wrap items-center gap-3"
            >
              <Link
                to="/demo"
                data-testid="hero-book-demo-btn"
                className="inline-flex items-center gap-1.5 bg-slate-900 text-white rounded-full px-6 py-3.5 text-sm font-semibold hover:bg-slate-800 transition-all shadow-sm hover:shadow-lg"
              >
                Book a Platform Demo <ArrowRight className="w-4 h-4" />
              </Link>
              <Link
                to="/programs"
                data-testid="hero-explore-programs-btn"
                className="inline-flex items-center gap-1.5 bg-white text-slate-900 border border-slate-200 rounded-full px-6 py-3.5 text-sm font-semibold hover:bg-slate-50 transition-colors"
              >
                Explore Cash-Pay Programs
              </Link>
            </motion.div>

            {/* Mini trust row */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.8, delay: 0.4 }}
              className="mt-10 flex items-center gap-6 text-xs text-slate-500"
            >
              <div className="flex items-center gap-2">
                <span className="w-6 h-6 rounded-full bg-emerald-50 border border-emerald-100 flex items-center justify-center">
                  <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
                </span>
                Minimal-PHI architecture
              </div>
              <div className="hidden sm:flex items-center gap-2">
                <span className="w-6 h-6 rounded-full bg-blue-50 border border-blue-100 flex items-center justify-center">
                  <span className="w-1.5 h-1.5 rounded-full bg-blue-500" />
                </span>
                Role-based access
              </div>
              <div className="hidden md:flex items-center gap-2">
                <span className="w-6 h-6 rounded-full bg-slate-50 border border-slate-200 flex items-center justify-center">
                  <span className="w-1.5 h-1.5 rounded-full bg-slate-500" />
                </span>
                Audit logging
              </div>
            </motion.div>
          </div>

          <div className="lg:col-span-6 relative">
            <DashboardMock />
          </div>
        </div>
      </div>
    </section>
  );
}
